import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { TabBar, TabBtn } from '../../components/common/Tabs';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { HiOutlineShieldCheck as IconShield, HiOutlineLockClosed as IconLock, HiOutlineAdjustments as IconSettings, HiOutlineGlobe } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { COUNTRY_LIST, getCountryDetails } from '../../config/countryConfig';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Org Admin' },
  { value: 'gate_guard', label: 'Gate Guard' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'production_manager', label: 'Production Manager' },
  { value: 'quality_checker', label: 'Quality Checker' },
  { value: 'store_manager', label: 'Store Manager' },
  { value: 'sales', label: 'Sales Officer' }
];

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/uploads/')) {
    const apiURL = process.env.REACT_APP_API_URL;
    if (apiURL) {
      const base = apiURL.endsWith('/api') ? apiURL : (apiURL.endsWith('/api/') ? apiURL.slice(0, -1) : `${apiURL}/api`);
      return `${base}${url}`;
    }
    if (window.location.port) {
      return `${window.location.protocol}//${window.location.hostname}:9898${url}`;
    } else {
      return `${window.location.origin}/api${url}`;
    }
  }
  return url;
};

const SettingsDashboard = () => {
  const { user } = useAuth();
  const { settings, updateSettings, reloadSettings } = useOrg();
  const isDragChain = user?.organizationId?.subIndustry === 'Drag Chain' || !user?.organizationId?.subIndustry;

  const [activeTab, setActiveTab] = useState('branding');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && (user.role === 'super_admin' || user.role === 'admin')) {
      setActiveTab('security');
    } else {
      setActiveTab('branding');
    }
  }, [user]);

  // 1. Security & Compliance states
  const [securitySettings, setSecuritySettings] = useState({
    disableScreenshots: true,
    requireBiometric: true,
    restrictCrossDepartment: true,
    allowMobileApp: false
  });

  // 2. Branding states
  const [brandName, setBrandName] = useState('');
  const [brandSubtitle, setBrandSubtitle] = useState('');
  const [logo, setLogo] = useState('');
  const [themeColor, setThemeColor] = useState('#f97316');
  const [footerText, setFooterText] = useState('');

  // 3. Sidebar Menus states
  const [menus, setMenus] = useState([]);

  // 4. Country & Regional Tax states
  const [selectedCountry, setSelectedCountry] = useState('India');

  // 5. Warehouse Physical Storage Structure states (Locations, Almirahs, Racks)
  const [warehouses, setWarehouses] = useState([]);
  const [almirahs, setAlmirahs] = useState([]);
  const [racks, setRacks] = useState([]);

  const [newWarehouse, setNewWarehouse] = useState('');
  const [newAlmirah, setNewAlmirah] = useState('');
  const [newRack, setNewRack] = useState('');

  // 6. Product Specs & Catalog Config states (Item Types, Units, Models, Inner Width, Radii)
  const [itemTypes, setItemTypes] = useState([]);
  const [stockUnits, setStockUnits] = useState([]);
  const [modelNumbers, setModelNumbers] = useState([]);
  const [innerSizes, setInnerSizes] = useState([]);
  const [bendingRadii, setBendingRadii] = useState([]);

  const [newItemType, setNewItemType] = useState('');
  const [newStockUnit, setNewStockUnit] = useState('');
  const [newModelNumber, setNewModelNumber] = useState('');
  const [newInnerSize, setNewInnerSize] = useState('');
  const [newBendingRadius, setNewBendingRadius] = useState('');

  useEffect(() => {
    if (settings) {
      setSelectedCountry(settings.country || 'India');
      setBrandName(settings.brandName || '');
      setBrandSubtitle(settings.brandSubtitle || '');
      setLogo(settings.logo || '');
      setThemeColor(settings.themeColor || '#f97316');
      setFooterText(settings.footerText || '');
      setMenus(settings.menus ? JSON.parse(JSON.stringify(settings.menus)) : []);

      setSecuritySettings({
        disableScreenshots: settings.disableScreenshots !== undefined ? settings.disableScreenshots : true,
        requireBiometric: settings.requireBiometric !== undefined ? settings.requireBiometric : true,
        restrictCrossDepartment: settings.restrictCrossDepartment !== undefined ? settings.restrictCrossDepartment : true,
        allowMobileApp: settings.allowMobileApp !== undefined ? settings.allowMobileApp : false
      });

      setWarehouses(settings.warehouses || []);
      setAlmirahs(settings.almirahs || []);
      setRacks(settings.racks || []);
    }
  }, [settings]);

  const handleSaveCountry = async () => {
    const specs = getCountryDetails(selectedCountry);
    const msg = `🌐 Confirm Country & Currency Switch:\n\nSwitching organization country to ${specs.country} will instantly update:\n- Currency: ${specs.currency} (${specs.symbol})\n- Tax: ${specs.taxName} (${specs.taxRate}%)\n\nAll real-time user sessions will be updated immediately. Proceed?`;
    if (!window.confirm(msg)) return;

    setSaving(true);
    try {
      await updateSettings({
        ...settings,
        country: selectedCountry
      });
      toast.success(`Country switched to ${specs.country}! Currency (${specs.symbol}) and ${specs.taxName} (${specs.taxRate}%) updated globally.`);
    } catch (err) {
      toast.error('Failed to update organization country');
    } finally {
      setSaving(false);
    }
  };

  const handleSecurityToggle = (key) => {
    setSecuritySettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveSecurity = async () => {
    const confirmMessage = "⚠️ Security Policy Update:\n\nSaving these updated policies will invalidate all active Android & iOS mobile sessions. Users logged in via the mobile app will be forced to re-login to sync these policies.\n\nDo you want to proceed?";
    if (!window.confirm(confirmMessage)) return;

    setSaving(true);
    try {
      await updateSettings({
        ...settings,
        disableScreenshots: securitySettings.disableScreenshots,
        requireBiometric: securitySettings.requireBiometric,
        restrictCrossDepartment: securitySettings.restrictCrossDepartment,
        allowMobileApp: securitySettings.allowMobileApp
      });
      toast.success('Global security policies applied successfully! All mobile sessions expired.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update security policies');
    } finally {
      setSaving(false);
    }
  };

  // Menu Handlers
  const handleMenuLabelChange = (index, val) => {
    const updated = [...menus];
    updated[index].label = val;
    setMenus(updated);
  };

  const handleMenuVisibilityToggle = (index) => {
    const updated = [...menus];
    updated[index].visible = !updated[index].visible;
    setMenus(updated);
  };

  const handleRoleToggle = (index, roleVal) => {
    const updated = [...menus];
    const rolesArray = updated[index].roles || [];
    if (rolesArray.includes(roleVal)) {
      updated[index].roles = rolesArray.filter(r => r !== roleVal);
    } else {
      updated[index].roles = [...rolesArray, roleVal];
    }
    setMenus(updated);
  };

  const handleSaveConfig = async () => {
    const confirmMessage = "⚠️ Branding Configuration Update:\n\nSaving these configuration changes will invalidate all active Android & iOS mobile sessions. Users logged in via the mobile app will be forced to re-login to synchronize layout updates.\n\nDo you want to proceed?";
    if (!window.confirm(confirmMessage)) return;

    setSaving(true);
    try {
      await updateSettings({
        brandName,
        brandSubtitle,
        logo,
        themeColor,
        footerText,
        menus
      });
      toast.success('Organization configuration updated successfully! All mobile sessions expired.');
      reloadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update configurations');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    if (!window.confirm('Are you sure you want to reset all branding settings and menus to defaults?')) return;
    setBrandName('TrackBells ERP');
    setBrandSubtitle('Factory Automation');
    setLogo('/logo.png');
    setThemeColor('#f97316');
    setFooterText('Powered by Cyberbells ITES services pvt ltd');
    setMenus([
      { key: 'gateGuard', label: 'Gate Operations', icon: 'HiOutlineTruck', path: '/gate-guard', visible: true, roles: ['super_admin', 'gate_guard'] },
      { key: 'productionManager', label: 'Production Manager', icon: 'HiOutlineCog', path: '/supervisor', visible: true, roles: ['super_admin', 'supervisor', 'production_manager'] },
      { key: 'qualityChecker', label: 'Quality Check', icon: 'HiOutlineClipboardCheck', path: '/quality', visible: true, roles: ['super_admin', 'quality_checker', 'supervisor', 'production_manager'] },
      { key: 'storeManager', label: 'Store & Godown', icon: 'HiOutlineCube', path: '/store', visible: true, roles: ['super_admin', 'store_manager'] },
      { key: 'dispatch', label: 'Dispatch & Shipping', icon: 'HiOutlineTruck', path: '/dispatch', visible: true, roles: ['super_admin', 'dispatch', 'dispatch_manager'] },
      { key: 'sales', label: 'Sales & Orders', icon: 'HiOutlineShoppingCart', path: '/sales', visible: true, roles: ['super_admin', 'sales'] },
      { key: 'purchase', label: 'Purchase & Procurement', icon: 'HiOutlineClipboardList', path: '/finance', visible: true, roles: ['super_admin', 'purchase_manager', 'purchase_user'] },
      { key: 'reports', label: 'Analytics & Reports', icon: 'HiOutlineDocumentText', path: '/reports', visible: true, roles: ['super_admin', 'admin', 'gate_guard', 'supervisor', 'production_manager', 'quality_checker', 'store_manager', 'sales', 'purchase_manager', 'purchase_user', 'dispatch'] },
      { key: 'users', label: 'User Management', icon: 'HiOutlineUsers', path: '/users', visible: true, roles: ['super_admin', 'admin'] },
      { key: 'optionalFeature', label: 'Optional Feature', icon: 'HiOutlineLightningBolt', path: '/optional-feature', visible: true, roles: ['super_admin', 'admin'] },
      { key: 'organizations', label: 'SaaS Tenants', icon: 'HiOutlineOfficeBuilding', path: '/admin/organizations', visible: true, roles: ['super_admin'] },
      { key: 'settings', label: 'Settings', icon: 'HiOutlineAdjustments', path: '/settings', visible: true, roles: ['super_admin', 'admin'] },
      { key: 'support', label: 'Help & Support', icon: 'HiOutlineDocumentReport', path: '/admin/support', visible: true, roles: ['super_admin'] }
    ]);
  };

  const handleAddWarehouse = () => {
    if (!newWarehouse.trim()) return;
    if (warehouses.includes(newWarehouse.trim())) {
      toast.error('Warehouse location already exists');
      return;
    }
    setWarehouses([...warehouses, newWarehouse.trim()]);
    setNewWarehouse('');
  };

  const handleRemoveWarehouse = (item) => {
    setWarehouses(warehouses.filter(w => w !== item));
  };

  const handleAddAlmirah = () => {
    if (!newAlmirah.trim()) return;
    if (almirahs.includes(newAlmirah.trim())) {
      toast.error('Almirah already exists');
      return;
    }
    setAlmirahs([...almirahs, newAlmirah.trim()]);
    setNewAlmirah('');
  };

  const handleRemoveAlmirah = (item) => {
    setAlmirahs(almirahs.filter(a => a !== item));
  };

  const handleAddRack = () => {
    if (!newRack.trim()) return;
    if (racks.includes(newRack.trim())) {
      toast.error('Rack / Shelf already exists');
      return;
    }
    setRacks([...racks, newRack.trim()]);
    setNewRack('');
  };

  const handleRemoveRack = (item) => {
    setRacks(racks.filter(r => r !== item));
  };

  const handleSaveWarehouseStructure = async () => {
    setSaving(true);
    try {
      await updateSettings({
        ...settings,
        warehouses,
        almirahs,
        racks
      });
      toast.success('Warehouse Physical Storage Structure updated in real-time!');
      reloadSettings();
    } catch (err) {
      toast.error('Failed to update Warehouse structure settings');
    } finally {
      setSaving(false);
    }
  };

  // Handlers for Product Specs Catalog
  const handleAddItemType = () => {
    if (!newItemType.trim()) return;
    const val = newItemType.trim().toLowerCase().replace(/\s+/g, '_');
    if (itemTypes.includes(val)) { toast.error('Item Type already exists'); return; }
    setItemTypes([...itemTypes, val]);
    setNewItemType('');
  };
  const handleRemoveItemType = (val) => setItemTypes(itemTypes.filter(t => t !== val));

  const handleAddStockUnit = () => {
    if (!newStockUnit.trim()) return;
    const val = newStockUnit.trim().toLowerCase();
    if (stockUnits.includes(val)) { toast.error('Stock Unit already exists'); return; }
    setStockUnits([...stockUnits, val]);
    setNewStockUnit('');
  };
  const handleRemoveStockUnit = (val) => setStockUnits(stockUnits.filter(u => u !== val));

  const handleAddModelNumber = () => {
    if (!newModelNumber.trim()) return;
    const val = newModelNumber.trim();
    if (modelNumbers.includes(val)) { toast.error('Model Number already exists'); return; }
    setModelNumbers([...modelNumbers, val]);
    setNewModelNumber('');
  };
  const handleRemoveModelNumber = (val) => setModelNumbers(modelNumbers.filter(m => m !== val));

  const handleAddInnerSize = () => {
    if (!newInnerSize.trim()) return;
    const val = newInnerSize.trim();
    if (innerSizes.includes(val)) { toast.error('Inner Width/Size already exists'); return; }
    setInnerSizes([...innerSizes, val]);
    setNewInnerSize('');
  };
  const handleRemoveInnerSize = (val) => setInnerSizes(innerSizes.filter(s => s !== val));

  const handleAddBendingRadius = () => {
    if (!newBendingRadius.trim()) return;
    const val = newBendingRadius.trim();
    if (bendingRadii.includes(val)) { toast.error('Bending Radius already exists'); return; }
    setBendingRadii([...bendingRadii, val]);
    setNewBendingRadius('');
  };
  const handleRemoveBendingRadius = (val) => setBendingRadii(bendingRadii.filter(r => r !== val));

  const handleSaveProductSpecs = async () => {
    setSaving(true);
    try {
      await updateSettings({
        ...settings,
        itemTypes,
        stockUnits,
        modelNumbers,
        innerSizes,
        bendingRadii
      });
      toast.success('Product Specifications & Catalogs updated in real-time!');
      reloadSettings();
    } catch (err) {
      toast.error('Failed to update Product Specs settings');
    } finally {
      setSaving(false);
    }
  };

  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <DashboardLayout pageTitle="Settings">
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--danger)' }}>
          <h3>Access Denied</h3>
          <p>Only the Organization Admin can access configuration settings.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Workspace Configuration">
      <div style={{ maxWidth: '1100px', margin: '0 auto', paddingBottom: '40px' }}>

        {/* TABS NAVBAR */}
        <TabBar>
          {(user?.role === 'super_admin' || user?.role === 'admin') && (
            <TabBtn active={activeTab === 'security'} onClick={() => setActiveTab('security')}>
              🛡️ Security & Compliance
            </TabBtn>
          )}
          {isDragChain && (
            <TabBtn active={activeTab === 'warehouse_structure'} onClick={() => setActiveTab('warehouse_structure')}>
              🏭 Warehouse Structure (Almirahs & Racks)
            </TabBtn>
          )}
          <TabBtn active={activeTab === 'branding'} onClick={() => setActiveTab('branding')}>
            🎨 Branding & Theme
          </TabBtn>
          <TabBtn active={activeTab === 'country'} onClick={() => setActiveTab('country')}>
            🌐 Country & Currency Tax
          </TabBtn>
          <TabBtn active={activeTab === 'menus'} onClick={() => setActiveTab('menus')}>
            📋 Sidebar & Menu Access
          </TabBtn>
        </TabBar>



        {activeTab === 'warehouse_structure' && (
          <div className="glass-card" style={{ padding: '32px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.2rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🏭 Warehouse & Physical Storage Structure Config
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '28px' }}>
              Configure physical Warehouse Locations, Almirahs / Cupboards, and Racks / Shelves (S.No) available across Godowns & Factory Floors.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>

              {/* 1. Warehouse Locations */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', borderRadius: '10px', padding: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: 'var(--primary)', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  🏢 1. Warehouse Locations (Godowns & Storage Areas)
                </h4>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter Warehouse Name (e.g. Central Store, Godown B)..."
                    value={newWarehouse}
                    onChange={e => setNewWarehouse(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddWarehouse())}
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="btn btn-primary" onClick={handleAddWarehouse}>+ Add Location</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {warehouses.map(w => (
                    <span key={w} style={{ background: 'rgba(249, 115, 22, 0.1)', border: '1px solid rgba(249, 115, 22, 0.3)', color: 'var(--text-primary)', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      📍 {w}
                      <button type="button" onClick={() => handleRemoveWarehouse(w)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem' }}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* 2. Almirah / Cupboards */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', borderRadius: '10px', padding: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#3b82f6', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  🗄️ 2. Almirah / Cupboards Catalog
                </h4>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter Almirah Name (e.g. ALMIRAH-19, CUPBOARD-B)..."
                    value={newAlmirah}
                    onChange={e => setNewAlmirah(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddAlmirah())}
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="btn btn-primary" onClick={handleAddAlmirah}>+ Add Almirah</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {almirahs.map(a => (
                    <span key={a} style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', color: 'var(--text-primary)', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      🗄️ {a}
                      <button type="button" onClick={() => handleRemoveAlmirah(a)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem' }}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* 3. Rack / Shelf (S.No) */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', borderRadius: '10px', padding: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#22c55e', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  📚 3. Rack / Shelf (S.No) Catalog
                </h4>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter Rack / Shelf Name (e.g. S.No 1, Shelf 4A)..."
                    value={newRack}
                    onChange={e => setNewRack(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddRack())}
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="btn btn-primary" onClick={handleAddRack}>+ Add Rack/Shelf</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {racks.map(r => (
                    <span key={r} style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', color: 'var(--text-primary)', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      📚 {r}
                      <button type="button" onClick={() => handleRemoveRack(r)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem' }}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button className="btn btn-primary" onClick={handleSaveWarehouseStructure} disabled={saving}>
                  {saving ? 'Saving Structure...' : 'Save Storage Structure Settings'}
                </button>
              </div>

            </div>
          </div>
        )}

        {activeTab === 'security' && (user?.role === 'super_admin' || user?.role === 'admin') && (
          <div className="glass-card" style={{ padding: '32px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.2rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IconShield style={{ color: 'var(--primary)' }} /> Security Settings
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '28px' }}>
              Manage organization-wide device and compliance parameters.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '32px' }}>
              <div style={settingRowStyle}>
                <div>
                  <div style={settingTitleStyle}>Disable Screenshots (Mobile App)</div>
                  <div style={settingDescStyle}>Enables FLAG_SECURE on mobile nodes to prevent visual data leaks.</div>
                </div>
                <ToggleSwitch checked={securitySettings.disableScreenshots} onChange={() => handleSecurityToggle('disableScreenshots')} />
              </div>

              <div style={settingRowStyle}>
                <div>
                  <div style={settingTitleStyle}>Require Biometric Login</div>
                  <div style={settingDescStyle}>Enforce face/fingerprint authentication on mobile scanner nodes.</div>
                </div>
                <ToggleSwitch checked={securitySettings.requireBiometric} onChange={() => handleSecurityToggle('requireBiometric')} />
              </div>

              <div style={settingRowStyle}>
                <div>
                  <div style={settingTitleStyle}>Strict Department Isolation</div>
                  <div style={settingDescStyle}>Prevent roles from accessing other department logs (e.g. sales views supervisor logs).</div>
                </div>
                <ToggleSwitch checked={securitySettings.restrictCrossDepartment} onChange={() => handleSecurityToggle('restrictCrossDepartment')} />
              </div>

              <div style={settingRowStyle}>
                <div>
                  <div style={settingTitleStyle}>Enable Mobile App Access</div>
                  <div style={settingDescStyle}>Permit employee connections via React Native application endpoints.</div>
                </div>
                <ToggleSwitch checked={securitySettings.allowMobileApp} onChange={() => handleSecurityToggle('allowMobileApp')} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={handleSaveSecurity} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconLock /> {saving ? 'Saving...' : 'Save Policies'}
              </button>
            </div>
          </div>
        )}

        {/* TAB: COUNTRY & CURRENCY TAX REGION */}
        {activeTab === 'country' && (
          <div className="glass-card" style={{ padding: '32px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.2rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🌐 Organization Country & Currency Tax Region
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '24px' }}>
              Changing country automatically updates currency symbols, tax rates (GST, VAT, Sales Tax), and price calculations across all user sessions in real-time.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '24px', alignItems: 'end', marginBottom: '24px' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Select Organization Country *</label>
                <select
                  className="form-input"
                  value={selectedCountry}
                  onChange={e => setSelectedCountry(e.target.value)}
                  style={{ cursor: 'pointer', height: '46px', fontSize: '1rem', fontWeight: 600 }}
                >
                  {COUNTRY_LIST.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <button
                className="btn btn-primary"
                onClick={handleSaveCountry}
                disabled={saving}
                style={{ height: '46px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '0.95rem' }}
              >
                <HiOutlineGlobe style={{ fontSize: '1.2rem' }} /> {saving ? 'Switching...' : 'Switch Country & Realtime Sync'}
              </button>
            </div>

            {/* Active Country Spec Summary */}
            {(() => {
              const specs = getCountryDetails(selectedCountry);
              return (
                <div style={{
                  background: 'rgba(249, 115, 22, 0.08)', border: '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '12px', padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Country</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px' }}>{specs.country} ({specs.code})</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Currency Code</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px' }}>{specs.currency}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Currency Symbol</div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--primary)', marginTop: '2px' }}>{specs.symbol}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Tax Type & Rate</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#22c55e', marginTop: '4px' }}>{specs.taxName} ({specs.taxRate}%)</div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* TAB 2: BRANDING & THEME */}
        {activeTab === 'branding' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '24px' }}>
            {/* Form inputs */}
            <div className="glass-card" style={{ padding: '32px' }}>
              <h3 style={{ color: 'var(--text-primary)', fontSize: '1.2rem', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🏢 Organization Branding
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '28px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Brand Name *</label>
                    <input className="form-input" value={brandName} onChange={e => setBrandName(e.target.value)} placeholder="e.g. TrackBells ERP" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Brand Subtitle</label>
                    <input className="form-input" value={brandSubtitle} onChange={e => setBrandSubtitle(e.target.value)} placeholder="e.g. Factory Automation" />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.5fr', gap: '16px', alignItems: 'end' }}>
                  <div className="form-group">
                    <label className="form-label">Logo (Emoji or Image URL) *</label>
                    <input className="form-input" value={logo} onChange={e => setLogo(e.target.value)} placeholder="e.g. 🏭 or https://..." required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Primary Color *</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input type="color" value={themeColor} onChange={e => setThemeColor(e.target.value)} style={{ width: '44px', height: '42px', padding: '0', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer' }} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{themeColor}</span>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Footer Copyright Text</label>
                  <input className="form-input" value={footerText} onChange={e => setFooterText(e.target.value)} placeholder="e.g. Powered by Cyberbells ITES..." />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" onClick={handleResetDefaults} style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>
                  Reset Defaults
                </button>
                <button className="btn btn-primary" onClick={handleSaveConfig} disabled={saving}>
                  {saving ? 'Updating Branding...' : 'Apply Branding & Theme'}
                </button>
              </div>
            </div>

            {/* Preview Panel */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div className="glass-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', background: '#f8fafc', border: '1px dashed var(--border)' }}>
                <h4 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px' }}>Sidebar Header Live Preview</h4>
                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid var(--border)', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: logo && (logo.startsWith('http') || logo.startsWith('/') || logo.startsWith('data:')) && !logo.includes('logo.png') ? '#ffffff' : `linear-gradient(135deg, ${themeColor}, ${themeColor}aa)`,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    color: '#fff',
                    padding: logo && (logo.startsWith('http') || logo.startsWith('/') || logo.startsWith('data:')) && !logo.includes('logo.png') ? '4px' : '0'
                  }}>
                    {logo && (logo.startsWith('http') || logo.startsWith('/') || logo.startsWith('data:')) ? (
                      <img src={getImageUrl(logo)} alt="Logo" style={{ maxHeight: '32px', maxWidth: '32px', borderRadius: '2px', objectFit: 'contain' }} />
                    ) : (
                      logo || '🏭'
                    )}
                  </div>
                  <div>
                    <h5 style={{ margin: 0, color: '#0f172a', fontSize: '0.95rem', fontWeight: 700 }}>{brandName || 'TrackBells ERP'}</h5>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>{brandSubtitle || 'Factory Automation'}</span>
                  </div>
                </div>
              </div>

              <div className="glass-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', background: '#f8fafc', border: '1px dashed var(--border)' }}>
                <h4 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px' }}>UI Accent Color Preview</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <button className="btn btn-primary" style={{ background: themeColor, borderColor: themeColor }} onClick={() => toast.success(`This is a preview of the Primary Button with theme color ${themeColor}`)}>Primary Button</button>
                  <button className="btn btn-secondary" style={{ color: themeColor, borderColor: themeColor }} onClick={() => toast.success(`This is a preview of the Outline Button with theme color ${themeColor}`)}>Outline Button</button>
                  <span style={{ display: 'inline-flex', padding: '4px 12px', borderRadius: '12px', background: `${themeColor}20`, color: themeColor, fontSize: '0.75rem', fontWeight: 600, alignItems: 'center', cursor: 'pointer' }} onClick={() => toast.success(`This is a preview of the Badge/Status color`)}>Active Status</span>
                </div>
              </div>

              <div className="glass-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', background: '#f8fafc', border: '1px dashed var(--border)' }}>
                <h4 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px' }}>Footer Live Preview</h4>
                <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid var(--border)', padding: '16px', textAlign: 'center', fontSize: '0.75rem', color: '#64748b' }}>
                  <p>© 2026 {brandName || 'TrackBells'}</p>
                  <p style={{ marginTop: '4px', fontWeight: 600, color: themeColor }}>{footerText || 'Powered by Cyberbells ITES services pvt ltd'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: SIDEBAR & MENU CONFIGURATOR */}
        {activeTab === 'menus' && (
          <div className="glass-card" style={{ padding: '32px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.2rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              📋 App Navigation Configuration
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '24px' }}>
              Rename menus, show/hide them globally, and select role checkboxes to dynamically configure which teams see which links.
            </p>

            <div style={{ overflowX: 'auto', marginBottom: '28px', border: '1px solid var(--border)', borderRadius: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--border-light)', borderBottom: '1px solid var(--border)' }}>
                    <th style={thStyle}>Menu Identifier</th>
                    <th style={thStyle}>Display Label</th>
                    <th style={thStyle}>Visibility</th>
                  </tr>
                </thead>
                <tbody>
                  {menus.map((menu, index) => (
                    <tr key={menu.key} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={tdCellStyle}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{menu.key}</div>
                        <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>Path: {menu.path}</div>
                      </td>
                      <td style={tdCellStyle}>
                        <input className="form-input" value={menu.label} onChange={e => handleMenuLabelChange(index, e.target.value)} style={{ padding: '6px 12px', fontSize: '0.85rem', width: '160px' }} />
                      </td>
                      <td style={tdCellStyle}>
                        <ToggleSwitch checked={menu.visible} onChange={() => handleMenuVisibilityToggle(index)} />
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
              <button type="button" className="btn btn-secondary" onClick={handleResetDefaults} style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>
                Reset Defaults
              </button>
              <button className="btn btn-primary" onClick={handleSaveConfig} disabled={saving}>
                {saving ? 'Saving Sidebar Config...' : 'Apply Sidebar Settings'}
              </button>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

// Styles
const settingRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingBottom: '20px',
  borderBottom: '1px solid var(--border-light)'
};

const settingTitleStyle = {
  color: 'var(--text-primary)',
  fontWeight: 600,
  fontSize: '1rem'
};

const settingDescStyle = {
  color: 'var(--text-secondary)',
  fontSize: '0.85rem',
  marginTop: '4px'
};

const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontWeight: 700,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  fontSize: '0.72rem',
  letterSpacing: '0.5px'
};

const tdCellStyle = {
  padding: '12px 16px',
  verticalAlign: 'middle',
  color: 'var(--text-secondary)'
};

const ToggleSwitch = ({ checked, onChange }) => (
  <div
    onClick={onChange}
    style={{
      width: '40px', height: '22px', background: checked ? 'var(--primary, #f97316)' : 'var(--bg-input, #e2e8f0)',
      borderRadius: '11px', position: 'relative', cursor: 'pointer', transition: 'background 0.3s'
    }}
  >
    <div style={{
      width: '18px', height: '18px', background: '#fff', borderRadius: '50%',
      position: 'absolute', top: '2px', left: checked ? '20px' : '2px', transition: 'left 0.3s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
    }} />
  </div>
);

export default SettingsDashboard;
